import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  MapPin, Star, ChevronRight, ChevronLeft, Clock, Calendar, 
  Truck, Navigation, TrendingUp, ShoppingBag, Ticket, BookOpen,
  CalendarDays, PartyPopper, Users, Hotel, Car, Ship, Key
} from 'lucide-react';
import { OFFICES } from '../data/database';
import OfficeCard from './OfficeCard';
import { useI18n } from '../i18n';

// Shared AutoSlider Component
export const AutoSlider = ({ children, autoScrollInterval = 3000, itemWidth = 280, className = '' }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const updateScrollButtons = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  }, []);

  const scroll = useCallback((direction) => {
    if (scrollRef.current) {
      const scrollAmount = itemWidth * 2;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  }, [itemWidth]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
    setIsPaused(true);
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) {
      setIsPaused(false);
      return;
    }
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      scroll(diff > 0 ? 'right' : 'left');
    }
    touchStartX.current = null;
    touchEndX.current = null;
    setIsPaused(false);
  };

  useEffect(() => {
    updateScrollButtons();
    const scrollEl = scrollRef.current;
    if (scrollEl) {
      scrollEl.addEventListener('scroll', updateScrollButtons);
      return () => scrollEl.removeEventListener('scroll', updateScrollButtons);
    }
  }, [updateScrollButtons]);

  useEffect(() => {
    if (!autoScrollInterval || isPaused) return;
    const interval = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        if (scrollLeft >= scrollWidth - clientWidth - 10) {
          scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scroll('right');
        }
      }
    }, autoScrollInterval);
    return () => clearInterval(interval);
  }, [autoScrollInterval, isPaused, scroll]);

  return (
    <div className={`relative group ${className}`} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>
      {canScrollLeft && (
        <button onClick={() => scroll('left')} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10 hidden lg:flex">
          <ChevronLeft className="w-6 h-6 text-gray-700" />
        </button>
      )}
      {canScrollRight && (
        <button onClick={() => scroll('right')} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10 hidden lg:flex">
          <ChevronRight className="w-6 h-6 text-gray-700" />
        </button>
      )}
    </div>
  );
};

// Offices Section (Vicino a te)
export const OfficesSection = ({ 
  offices, 
  title, 
  icon: Icon = MapPin,
  iconColor = "text-blue-500",
  onNavigate, 
  showViewAll = true,
  onViewAll 
}) => {
  const { t } = useI18n();
  const displayTitle = title || t('homepage', 'nearYou');
  if (!offices || offices.length === 0) return null;
  
  return (
    <div className="mb-6 lg:mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <h2 className="text-lg lg:text-xl font-bold text-gray-900">{displayTitle}</h2>
        </div>
        {showViewAll && onViewAll && (
          <button 
            onClick={onViewAll}
            className="text-emerald-600 text-sm font-medium flex items-center gap-1 hover:text-emerald-700"
          >
            {t('common', 'viewAll')} <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <AutoSlider autoScrollInterval={5000} itemWidth={290}>
        {offices.map((office) => (
          <div key={office.id} className="w-[280px] sm:w-[300px] lg:w-[320px] flex-shrink-0">
            <OfficeCard office={office} onClick={() => onNavigate('office', { id: office.id })} />
          </div>
        ))}
      </AutoSlider>
    </div>
  );
};

// Legacy alias for backward compatibility
export const BusinessesSection = OfficesSection;

// Services Section
export const ServicesSection = ({ 
  services, 
  title,
  subtitle,
  icon: Icon = TrendingUp,
  iconColor = "text-emerald-500",
  onNavigate, 
  showViewAll = true,
  onViewAll 
}) => {
  const { t } = useI18n();
  const displayTitle = title || t('misc', 'trendingServices');
  const displaySubtitle = subtitle || t('misc', 'inYourArea');
  if (!services || services.length === 0) return null;
  
  return (
    <div className="mb-6 lg:mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <h2 className="text-lg lg:text-xl font-bold text-gray-900">
            {displayTitle} {displaySubtitle && <span className="text-emerald-600 font-normal">{displaySubtitle}</span>}
          </h2>
        </div>
        {showViewAll && onViewAll && (
          <button 
            onClick={onViewAll}
            className="text-emerald-600 text-sm font-medium flex items-center gap-1 hover:text-emerald-700"
          >
            {t('common', 'viewAll')} <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <AutoSlider autoScrollInterval={2000} itemWidth={280}>
        {services.map((service) => {
          const office = OFFICES.find(o => o.id === service.officeId);
          return (
            <div 
              key={service.id}
              onClick={() => onNavigate('service', { id: service.id })}
              className="w-[220px] sm:w-[260px] lg:w-[280px] flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-emerald-200 hover:shadow-lg transition-all cursor-pointer group"
              data-testid={`service-${service.id}`}
            >
              <div className="relative h-32 lg:h-36">
                <img 
                  src={service.images?.[0] || service.image || office?.images?.[0] || 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400'} 
                  alt={service.name} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                />
                {office?.distance && (
                  <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-emerald-500 text-white px-2 py-1 rounded-full text-xs">
                    <Navigation className="w-3 h-3" /> {office.distance}
                  </div>
                )}
              </div>
              <div className="p-3 lg:p-4">
                <h4 className="font-semibold text-gray-900 text-sm lg:text-base truncate">{service.name}</h4>
                <p className="text-xs text-gray-500 truncate">{office?.name}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-bold text-emerald-600 text-sm lg:text-base">€{service.price}</span>
                  <div className="flex items-center gap-1 text-gray-400">
                    <Clock className="w-3 h-3" />
                    <span className="text-xs">{service.duration}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </AutoSlider>
    </div>
  );
};

// Products Section
export const ProductsSection = ({ 
  products, 
  title,
  icon: Icon = ShoppingBag,
  iconColor = "text-orange-500",
  onNavigate, 
  showViewAll = true,
  onViewAll 
}) => {
  const { t } = useI18n();
  const displayTitle = title || t('misc', 'popularProducts');
  if (!products || products.length === 0) return null;
  
  return (
    <div className="mb-6 lg:mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <h2 className="text-lg lg:text-xl font-bold text-gray-900">{displayTitle}</h2>
        </div>
        {showViewAll && onViewAll && (
          <button 
            onClick={onViewAll}
            className="text-emerald-600 text-sm font-medium flex items-center gap-1 hover:text-emerald-700"
          >
            {t('common', 'viewAll')} <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <AutoSlider autoScrollInterval={2800} itemWidth={180}>
        {products.map((product) => {
          const office = OFFICES.find(o => o.id === product.officeId);
          return (
            <div 
              key={product.id}
              onClick={() => onNavigate('product', { id: product.id })}
              className="w-[160px] sm:w-[180px] lg:w-[220px] flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-emerald-200 hover:shadow-lg transition-all cursor-pointer group"
              data-testid={`product-${product.id}`}
            >
              <div className="relative aspect-square">
                <img src={product.images?.[0] || product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                {product.discount && (
                  <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">-{product.discount}%</div>
                )}
                <div className="absolute bottom-2 left-2 right-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 w-fit ${
                    product.shipping === 'delivery' || product.shippingType === 'delivery' ? 'bg-green-100 text-green-700' : 
                    product.shipping === 'pickup' || product.shippingType === 'pickup' ? 'bg-orange-100 text-orange-700' : 
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {product.shipping === 'delivery' || product.shippingType === 'delivery' ? (
                      <><Truck className="w-3 h-3" /> {t('shipping', 'delivery')}</>
                    ) : product.shipping === 'pickup' || product.shippingType === 'pickup' ? (
                      <><MapPin className="w-3 h-3" /> {t('shipping', 'pickup')}</>
                    ) : (
                      <><Truck className="w-3 h-3" /> {t('shipping', 'both')}</>
                    )}
                  </span>
                </div>
              </div>
              <div className="p-3 lg:p-4">
                <h4 className="font-medium text-gray-900 text-sm lg:text-base truncate">{product.name}</h4>
                <p className="text-xs sm:text-sm text-gray-500 truncate">{office?.name}</p>
                <div className="flex items-center gap-2 mt-2">
                  <p className="font-bold text-emerald-600 text-sm lg:text-lg">€{product.price}</p>
                  {product.originalPrice && <p className="text-xs text-gray-400 line-through">€{product.originalPrice}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </AutoSlider>
    </div>
  );
};

// Events Section
export const EventsSection = ({ 
  events, 
  title,
  icon: Icon = Ticket,
  iconColor = "text-purple-500",
  onNavigate, 
  showViewAll = true,
  onViewAll 
}) => {
  const { t } = useI18n();
  const displayTitle = title || t('misc', 'recommendedEvents');
  if (!events || events.length === 0) return null;
  
  return (
    <div className="mb-6 lg:mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <h2 className="text-lg lg:text-xl font-bold text-gray-900">{displayTitle}</h2>
        </div>
        {showViewAll && onViewAll && (
          <button 
            onClick={onViewAll}
            className="text-emerald-600 text-sm font-medium flex items-center gap-1 hover:text-emerald-700"
          >
            {t('common', 'viewAll')} <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <AutoSlider autoScrollInterval={3500} itemWidth={280}>
        {events.map((event) => (
          <div 
            key={event.id}
            onClick={() => onNavigate('event', { id: event.id })}
            className="w-[260px] sm:w-[280px] lg:w-[300px] flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-purple-200 hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="relative h-32 lg:h-40 overflow-hidden">
              <img src={event.images?.[0] || event.image} alt={event.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              <div className="absolute top-3 left-3 flex gap-2">
                <span className="bg-purple-500 text-white text-xs font-medium px-2 py-1 rounded-full">{event.category}</span>
              </div>
              <div className="absolute bottom-3 left-3 flex gap-1.5">
                <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                  event.locationType === 'online' ? 'bg-blue-500 text-white' : 'bg-white/90 backdrop-blur-sm text-gray-700'
                }`}>
                  {event.locationType === 'online' ? (
                    <><span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span> Online</>
                  ) : (
                    <><MapPin className="w-3 h-3" /> {event.city}</>
                  )}
                </span>
                {event.locationType === 'in-person' && event.distance && (
                  <span className="text-xs px-2 py-1 rounded-full flex items-center gap-1 bg-emerald-500 text-white">
                    <Navigation className="w-3 h-3" /> {event.distance}
                  </span>
                )}
              </div>
            </div>
            <div className="p-3 lg:p-4">
              <h4 className="font-semibold text-gray-900 truncate text-sm lg:text-base">{event.name}</h4>
              <div className="flex items-center gap-2 mt-2 text-xs lg:text-sm text-gray-500">
                <Calendar className="w-3 h-3 lg:w-4 lg:h-4" />
                <span>{new Date(event.date).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
              </div>
              <div className="flex items-center justify-between mt-2 lg:mt-3">
                <p className="font-bold text-purple-600 text-sm lg:text-lg">{event.price === 0 ? t('misc', 'free') : `€${event.price}`}</p>
                <span className="text-xs text-gray-400">{event.maxParticipants - event.currentParticipants} {t('misc', 'spots')}</span>
              </div>
            </div>
          </div>
        ))}
      </AutoSlider>
    </div>
  );
};

// Courses Section
export const CoursesSection = ({ 
  courses, 
  title,
  icon: Icon = BookOpen,
  iconColor = "text-indigo-500",
  onNavigate, 
  showViewAll = true,
  onViewAll 
}) => {
  const { t } = useI18n();
  const displayTitle = title || t('misc', 'coursesForYou');
  if (!courses || courses.length === 0) return null;
  
  return (
    <div className="mb-6 lg:mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <h2 className="text-lg lg:text-xl font-bold text-gray-900">{displayTitle}</h2>
        </div>
        {showViewAll && onViewAll && (
          <button 
            onClick={onViewAll}
            className="text-emerald-600 text-sm font-medium flex items-center gap-1 hover:text-emerald-700"
          >
            {t('common', 'viewAll')} <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <AutoSlider autoScrollInterval={4500} itemWidth={280}>
        {courses.map((course) => (
          <div 
            key={course.id}
            onClick={() => onNavigate('course', { id: course.id })}
            className="w-[260px] sm:w-[280px] lg:w-[300px] flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-indigo-200 hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="relative h-32 lg:h-40 overflow-hidden">
              <img src={course.images?.[0] || course.image} alt={course.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              <div className="absolute top-3 left-3 flex gap-2">
                <span className="bg-indigo-500 text-white text-xs font-medium px-2 py-1 rounded-full">{course.category}</span>
                <span className="bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium px-2 py-1 rounded-full">{course.level}</span>
              </div>
              <div className="absolute bottom-3 left-3 flex gap-1.5">
                <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                  course.locationType === 'online' ? 'bg-blue-500 text-white' : 'bg-white/90 backdrop-blur-sm text-gray-700'
                }`}>
                  {course.locationType === 'online' ? (
                    <><span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span> Online</>
                  ) : (
                    <><MapPin className="w-3 h-3" /> {course.city}</>
                  )}
                </span>
                {course.locationType === 'in-person' && course.distance && (
                  <span className="text-xs px-2 py-1 rounded-full flex items-center gap-1 bg-emerald-500 text-white">
                    <Navigation className="w-3 h-3" /> {course.distance}
                  </span>
                )}
              </div>
            </div>
            <div className="p-3 lg:p-4">
              <h4 className="font-semibold text-gray-900 truncate text-sm lg:text-base">{course.name}</h4>
              <div className="flex items-center gap-2 lg:gap-3 mt-2 text-xs lg:text-sm text-gray-500">
                <span>{course.totalHours} ore</span>
                <span>•</span>
                <span>{course.lessons} {t('misc', 'lessons')}</span>
              </div>
              <div className="flex items-center justify-between mt-2 lg:mt-3">
                <p className="font-bold text-indigo-600 text-sm lg:text-lg">€{course.price}</p>
                {course.originalPrice && <p className="text-xs text-gray-400 line-through">€{course.originalPrice}</p>}
              </div>
            </div>
          </div>
        ))}
      </AutoSlider>
    </div>
  );
};


// ============================================================================
// NUOVE SEZIONI - Stile Homepage Aggiornata
// ============================================================================

// Appointment Services Section (Servizi ad Appuntamento)
export const AppointmentServicesSection = ({ 
  services, 
  title = "Servizi ad Appuntamento",
  badge = "Prenotazioni orarie",
  onNavigate, 
  showViewAll = true,
  onViewAll 
}) => {
  const { t } = useI18n();
  if (!services || services.length === 0) return null;
  
  return (
    <div className="mb-6 lg:mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-emerald-500" />
          <h2 className="text-lg lg:text-xl font-bold text-gray-900">{title}</h2>
          {badge && (
            <span className="hidden sm:inline-block text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {badge}
            </span>
          )}
        </div>
        {showViewAll && onViewAll && (
          <button 
            onClick={onViewAll}
            className="text-emerald-600 text-sm font-medium flex items-center gap-1 hover:text-emerald-700"
          >
            {t('common', 'viewAll')} <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <AutoSlider autoScrollInterval={2000} itemWidth={280}>
        {services.map((service) => {
          const office = OFFICES.find(o => o.id === service.officeId);
          return (
            <div 
              key={service.id}
              onClick={() => onNavigate('service-detail', { id: service.id })}
              className="w-[220px] sm:w-[260px] lg:w-[280px] flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-emerald-200 hover:shadow-lg transition-all cursor-pointer group"
              data-testid={`service-${service.id}`}
            >
              <div className="relative h-32 lg:h-36">
                <img 
                  src={service.images?.[0] || service.image || office?.images?.[0] || office?.image || 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400'} 
                  alt={service.name} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                />
                {office?.distance && (
                  <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-emerald-500 text-white px-2 py-1 rounded-full text-xs">
                    <Navigation className="w-3 h-3" /> {office.distance}
                  </div>
                )}
              </div>
              <div className="p-3 lg:p-4">
                <h4 className="font-semibold text-gray-900 text-sm lg:text-base truncate">{service.name}</h4>
                <p className="text-xs text-gray-500 truncate">{office?.name}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-bold text-emerald-600 text-sm lg:text-base">€{service.price}</span>
                  <div className="flex items-center gap-1 text-gray-400">
                    <Clock className="w-3 h-3" />
                    <span className="text-xs">{service.duration} min</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </AutoSlider>
    </div>
  );
};

// Rentals Section (Noleggi e Soggiorni)
export const RentalsSection = ({ 
  rentals, 
  title = "Noleggi e Soggiorni",
  badge = "Prenotazioni giornaliere",
  onNavigate, 
  showViewAll = true,
  onViewAll 
}) => {
  const { t } = useI18n();
  if (!rentals || rentals.length === 0) return null;
  
  return (
    <div className="mb-6 lg:mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-blue-500" />
          <h2 className="text-lg lg:text-xl font-bold text-gray-900">{title}</h2>
          {badge && (
            <span className="hidden sm:inline-block text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {badge}
            </span>
          )}
        </div>
        {showViewAll && onViewAll && (
          <button 
            onClick={onViewAll}
            className="text-blue-600 text-sm font-medium flex items-center gap-1 hover:text-blue-700"
          >
            {t('common', 'viewAll')} <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <AutoSlider autoScrollInterval={4000} itemWidth={320}>
        {rentals.map((rental) => {
          const office = OFFICES.find(o => o.id === rental.officeId);
          const price = rental.pricePerNight || rental.pricePerDay;
          const priceLabel = rental.pricePerNight ? '/notte' : '/giorno';
          const isHotel = rental.pricePerNight;
          const isCar = ['economy', 'premium', 'suv', 'electric'].includes(rental.category);
          const isBoat = ['gozzo', 'yacht', 'gommone', 'catamarano'].includes(rental.category);
          
          return (
            <div 
              key={`rental-${rental.id}`}
              onClick={() => onNavigate('rental-detail', { id: rental.id })}
              className="w-[300px] sm:w-[320px] lg:w-[340px] flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-blue-200 hover:shadow-lg transition-all cursor-pointer group"
              data-testid={`rental-${rental.id}`}
            >
              <div className="relative h-40 lg:h-48">
                <img 
                  src={rental.images?.[0] || 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800'} 
                  alt={rental.name} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                />
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5 ${
                    isHotel ? 'bg-blue-500 text-white' : 
                    isCar ? 'bg-purple-500 text-white' : 
                    isBoat ? 'bg-cyan-500 text-white' : 
                    'bg-emerald-500 text-white'
                  }`}>
                    {isHotel ? <Hotel className="w-3.5 h-3.5" /> : 
                     isCar ? <Car className="w-3.5 h-3.5" /> : 
                     isBoat ? <Ship className="w-3.5 h-3.5" /> : 
                     <Key className="w-3.5 h-3.5" />}
                    {rental.category}
                  </span>
                </div>
                {(rental.minStay > 1 || rental.minDays > 1) && (
                  <div className="absolute top-3 right-3">
                    <span className="text-xs bg-white/90 backdrop-blur-sm text-gray-700 px-2 py-1 rounded-full">
                      Min {rental.minStay || rental.minDays} {rental.minStay ? 'notti' : 'giorni'}
                    </span>
                  </div>
                )}
                {(rental.maxGuests || rental.maxPassengers) && (
                  <div className="absolute bottom-3 left-3">
                    <span className="text-xs bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded-full flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      Max {rental.maxGuests || rental.maxPassengers}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-4">
                <h4 className="font-semibold text-gray-900 text-base truncate">{rental.name}</h4>
                <p className="text-sm text-gray-500 truncate mt-0.5">{office?.name}</p>
                {rental.description && (
                  <p className="text-xs text-gray-400 mt-1 line-clamp-1">{rental.description}</p>
                )}
                <div className="flex items-center justify-between mt-3">
                  <div>
                    <span className="font-bold text-blue-600 text-lg">€{price}</span>
                    <span className="text-gray-500 text-sm">{priceLabel}</span>
                  </div>
                  {rental.amenities && (
                    <div className="flex items-center gap-1">
                      {rental.amenities.slice(0, 2).map((amenity, i) => (
                        <span key={i} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                          {amenity}
                        </span>
                      ))}
                      {rental.amenities.length > 2 && (
                        <span className="text-[10px] text-gray-400">+{rental.amenities.length - 2}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </AutoSlider>
    </div>
  );
};

// Upcoming Events Section (Prossimi Eventi)
export const UpcomingEventsSection = ({ 
  events, 
  title = "Prossimi Eventi",
  badge = "Esperienze uniche",
  onNavigate, 
  showViewAll = true,
  onViewAll 
}) => {
  const { t } = useI18n();
  if (!events || events.length === 0) return null;
  
  return (
    <div className="mb-6 lg:mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <PartyPopper className="w-5 h-5 text-rose-500" />
          <h2 className="text-lg lg:text-xl font-bold text-gray-900">{title}</h2>
          {badge && (
            <span className="hidden sm:inline-block text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {badge}
            </span>
          )}
        </div>
        {showViewAll && onViewAll && (
          <button 
            onClick={onViewAll}
            className="text-rose-600 text-sm font-medium flex items-center gap-1 hover:text-rose-700"
          >
            {t('common', 'viewAll')} <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      <AutoSlider autoScrollInterval={4500} itemWidth={340}>
        {events.map((event) => {
          const office = OFFICES.find(o => o.id === event.officeId);
          const spotsLeft = event.maxParticipants - event.currentParticipants;
          const isAlmostFull = spotsLeft <= 5;
          const isFree = event.price === 0;
          
          return (
            <div 
              key={`event-${event.id}`}
              onClick={() => onNavigate('event-detail', { id: event.id })}
              className="w-[320px] sm:w-[340px] lg:w-[380px] flex-shrink-0 bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-rose-200 hover:shadow-lg transition-all cursor-pointer group"
              data-testid={`event-${event.id}`}
            >
              <div className="relative h-44 lg:h-52">
                <img 
                  src={event.image} 
                  alt={event.name} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform" 
                />
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5 ${
                    event.category === 'wellness' ? 'bg-purple-500 text-white' : 
                    event.category === 'fitness' ? 'bg-orange-500 text-white' : 
                    event.category === 'workshop' ? 'bg-blue-500 text-white' :
                    'bg-rose-500 text-white'
                  }`}>
                    <PartyPopper className="w-3.5 h-3.5" />
                    {event.category}
                  </span>
                  {isFree && (
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-500 text-white">
                      GRATIS
                    </span>
                  )}
                </div>
                {isAlmostFull && (
                  <div className="absolute top-3 right-3">
                    <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full animate-pulse">
                      Solo {spotsLeft} posti!
                    </span>
                  </div>
                )}
                <div className="absolute bottom-3 left-3">
                  <span className="text-xs bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-full flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(event.date).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
                    <span className="text-white/70">•</span>
                    {event.startTime}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <h4 className="font-semibold text-gray-900 text-base truncate">{event.name}</h4>
                <p className="text-sm text-gray-500 truncate mt-0.5">{office?.name}</p>
                <p className="text-xs text-gray-400 mt-1 line-clamp-2">{event.description}</p>
                <div className="flex items-center justify-between mt-3">
                  <div>
                    {isFree ? (
                      <span className="font-bold text-emerald-600 text-lg">Gratuito</span>
                    ) : (
                      <>
                        <span className="font-bold text-rose-600 text-lg">€{event.price}</span>
                        <span className="text-gray-500 text-sm">/persona</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-gray-500">
                    <Users className="w-4 h-4" />
                    <span className="text-sm">{event.currentParticipants}/{event.maxParticipants}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </AutoSlider>
    </div>
  );
};

